import { useState, useCallback, useEffect } from 'react';
import { _addNote, _getNotes, _updateNote, _deleteNote } from 'controllers/notes/notes_controllers';
import { _getExecutionsByLeadId } from 'controllers/leads/leads_controller';
import { getResponseValue } from 'util/validate';
import { getProfile } from 'util/auth';

export function useNotes(lead) {
  const [notes, setNotes] = useState([]);
  const [history, setHistory] = useState([]);
  const [fullNotes, setFullNotes] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  const fetchNotes = useCallback(
    async (showLoading = true) => {
      if (showLoading) setLoading(true);
      setError(null);
      try {
        if (lead?.notes) {
          setNotes(lead.notes);
        } else {
          const res = await _getNotes('lead', lead.id);
          let notesTemp = await getResponseValue(res);
          const messages = await _getExecutionsByLeadId(lead.school_id, lead.id);
          setHistory(messages);
          setNotes(notesTemp);
          let allNotes = [...notesTemp, ...messages];
          allNotes.sort((a, b) => (a.created_at > b.created_at ? -1 : a.created_at < b.created_at ? 1 : 0));
          setFullNotes(allNotes);
        }
        setInitialized(true);
      } catch (err) {
        setError(err);
      } finally {
        if (showLoading) setLoading(false);
      }
    },
    [lead]
  );

  useEffect(() => {
    fetchNotes(true); // show skeleton on initial load
  }, [lead]);

  const createNode = useCallback(
    async ({ leadId, note }) => {
      const user = getProfile();
      setLoading(true);
      try {
        const payload = new FormData();
        payload.append('object_id', leadId);
        payload.append('object_type', 'lead');
        payload.append('user_id', user.email);
        payload.append('note', note);

        const response = await _addNote(payload);
        if (response.ok) {
          await fetchNotes(false); // don't show skeleton on add
        }
      } catch (err) {
        console.error(err);
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [lead, fetchNotes]
  );

  const updateNote = useCallback(
    async ({ noteId, note }) => {
      setError(null);
      try {
        const response = await _updateNote(noteId, note);
        if (response.ok) {
          await fetchNotes(false); // don't show skeleton on update
        }
      } catch (err) {
        setError(err);
      }
    },
    [fetchNotes]
  );

  const removeNote = useCallback(
    async (noteId) => {
      setError(null);
      try {
        const response = await _deleteNote(noteId);
        if (response.ok) {
          await fetchNotes(false); // don't show skeleton on remove
        }
      } catch (err) {
        setError(err);
      }
    },
    [fetchNotes]
  );

  return { notes, history, fullNotes, createNode, updateNote, removeNote, fetchNotes, loading, initialized };
}
